import {
  Callout,
  DirectionalHint,
  Persona,
  PersonaPresence,
  PersonaSize,
  PrimaryButton,
} from "@fluentui/react";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IUser } from "../../Models/IUser";
import { GlobalState } from "../../Redux/RootReducer";
import { IRole } from "../../Models/IRole";
import { AppRoutes } from "../../Constants/AppRoutes";
import "./BetterPersona.scss";

const BetterPersona = () => {
  const navigate = useNavigate();
  const tooltipId = "user-tooltip-main-item";
  const user: IUser | undefined = useSelector(
    (s: GlobalState) => s.generic.user
  );

  const getUserInitials = () => {
    if (!user?.identifier) return "";

    const parts = user.identifier.includes(" ")
      ? user.identifier.split(" ")
      : user.identifier.split(".");
    return parts
      .map((part) => part[0]?.toLocaleUpperCase() || "")
      .slice(0, 2)
      .join("");
  };

  // ruoli scritti per chi legge, non i codici del database
  const ROLE_NAMES: Record<string, string> = { superAdmin: "Amministratore", installer: "Installatore", superOperator: "Supervisore", operator: "Familiare / assistente" };
  const roles =
    user?.roles?.map((role: IRole) => ROLE_NAMES[role.type] ?? role.type) || [];

  const tooltipContent = (
    <div className="tooltip-content" onClick={(e) => e.stopPropagation()}>
      <div style={{ marginBottom: "1em" }}>
        <strong>{user?.identifier}</strong>
        <div>{roles.join(", ")}</div>
      </div>
      <PrimaryButton
        text="Impostazioni"
        onClick={(e) => {
          e.stopPropagation();
          navigate(AppRoutes.APP_OPTIONS_PAGE);
        }}
        iconProps={{ iconName: "Settings" }}
        styles={{ root: { marginBottom: "0.5em" } }}
      />
      <PrimaryButton
        text="Esci"
        onClick={(e) => {
          e.stopPropagation();
          navigate(AppRoutes.APP_LOGOUT_PAGE);
        }}
        iconProps={{ iconName: "OutOfOffice" }}
      />
    </div>
  );

  // Menu dell'avatar. Prima era un tooltip, che si apre al passaggio del mouse: sul telefono il tocco a volte non lo
  // apriva o lo richiudeva subito (titolare, 01/11/2026). Ora il tocco o il clic lo apre e lo chiude, il clic fuori lo
  // chiude; sul PC si apre anche passandoci sopra col mouse, come prima.
  const [open, setOpen] = useState<boolean>(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const canHover = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? false;
  const keepOpen = () => {
    window.clearTimeout(closeTimer.current);
    if (canHover) setOpen(true);
  };
  const closeLater = () => {
    if (!canHover) return;
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 250);
  };
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className="persona-wrap"
        id={tooltipId}
        aria-label="Menu utente"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => {
          window.clearTimeout(closeTimer.current);
          // col mouse il menu e' gia' aperto dal passaggio: il clic non deve richiuderlo
          setOpen((o) => (canHover ? true : !o));
        }}
        onMouseEnter={keepOpen}
        onMouseLeave={closeLater}
      >
        <Persona
          imageInitials={getUserInitials()}
          text={user?.identifier}
          tertiaryText={roles.join(", ")}
          hidePersonaDetails
          size={PersonaSize.size32}
          presence={PersonaPresence.online}
          initialsColor="#007038"
        />
      </button>
      {open && (
        <Callout
          target={`#${tooltipId}`}
          gapSpace={0}
          isBeakVisible
          directionalHint={DirectionalHint.bottomRightEdge}
          setInitialFocus
          onDismiss={() => setOpen(false)}
          onMouseEnter={keepOpen}
          onMouseLeave={closeLater}
        >
          <div className="persona-menu">{tooltipContent}</div>
        </Callout>
      )}
    </div>
  );
};

export default BetterPersona;
